import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  Tags,
  Loader2,
  Plus,
  Edit,
  Trash2,
  Palette
} from "lucide-react";

const COLORS = [
  "#6366f1", "#8b5cf6", "#a855f7", "#d946ef", "#ec4899",
  "#f43f5e", "#ef4444", "#f97316", "#f59e0b", "#eab308",
  "#84cc16", "#22c55e", "#10b981", "#14b8a6", "#06b6d4",
  "#0ea5e9", "#3b82f6", "#6366f1"
];

const emptyForm = {
  nombre: "",
  descripcion: "",
  color: "#6366f1"
};

export default function CategoriasInventarioPage() {
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const { getAuthHeaders } = useAuth();

  const fetchCategorias = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/inventario/categorias`, getAuthHeaders());
      setCategorias(response.data);
    } catch (err) {
      console.error("Error fetching categories:", err);
      toast.error("Error al cargar categorías");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchCategorias();
  }, [fetchCategorias]);

  const handleSave = async () => {
    if (!formData.nombre.trim()) {
      toast.error("El nombre es requerido");
      return;
    }

    try {
      setSaving(true);
      if (editingItem) {
        await axios.put(`${API}/inventario/categorias/${editingItem.id}`, formData, getAuthHeaders());
        toast.success("Categoría actualizada");
      } else {
        await axios.post(`${API}/inventario/categorias`, formData, getAuthHeaders());
        toast.success("Categoría creada");
      }
      setShowDialog(false);
      setFormData(emptyForm);
      setEditingItem(null);
      fetchCategorias();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      nombre: item.nombre || "",
      descripcion: item.descripcion || "",
      color: item.color || "#6366f1"
    });
    setShowDialog(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`¿Eliminar la categoría "${item.nombre}"?`)) return;
    
    try {
      await axios.delete(`${API}/inventario/categorias/${item.id}`, getAuthHeaders());
      toast.success("Categoría eliminada");
      fetchCategorias();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="categorias-inventario-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
              <Tags className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
                Categorías de Inventario
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Clasificación de artículos
              </p>
            </div>
          </div>
          <Button onClick={() => { setEditingItem(null); setFormData(emptyForm); setShowDialog(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Nueva Categoría
          </Button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Color</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categorias.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-12">
                      <Tags className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                      <p className="text-slate-500">No hay categorías creadas</p>
                      <Button 
                        variant="link" 
                        onClick={() => { setEditingItem(null); setFormData(emptyForm); setShowDialog(true); }}
                      >
                        Crear la primera categoría
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  categorias.map((cat) => (
                    <TableRow key={cat.id}>
                      <TableCell>
                        <div 
                          className="w-8 h-8 rounded-full"
                          style={{ backgroundColor: cat.color || "#6366f1" }}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{cat.nombre}</TableCell>
                      <TableCell className="text-slate-500">{cat.descripcion || "—"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(cat)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(cat)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <div className="p-4 border-t text-sm text-slate-500">
              {categorias.length} categoría{categorias.length !== 1 ? "s" : ""}
            </div>
          </Card>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingItem ? "Editar Categoría" : "Nueva Categoría"}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Nombre de la categoría"
                />
              </div>
              <div className="space-y-2">
                <Label>Descripción</Label>
                <Textarea
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  placeholder="Descripción opcional"
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  Color
                </Label>
                <div className="flex flex-wrap gap-2">
                  {COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`w-8 h-8 rounded-full transition-transform ${
                        formData.color === color ? "ring-2 ring-offset-2 ring-slate-500 scale-110" : "hover:scale-110"
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => setFormData({ ...formData, color })}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <div 
                    className="w-8 h-8 rounded-full border"
                    style={{ backgroundColor: formData.color }}
                  />
                  <Input
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-28 font-mono text-sm"
                    placeholder="#6366f1"
                  />
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingItem ? "Guardar Cambios" : "Crear Categoría"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
